import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Play, 
  Pause, 
  SkipForward,
  Target,
  Lightbulb,
  CheckCircle
} from 'lucide-react';
import { Card } from '@plvplus/core';

export interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string; // CSS selector
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: {
    type: 'click' | 'hover' | 'focus' | 'scroll';
    element?: string;
    description: string;
  };
  highlight?: {
    element: string;
    style?: 'pulse' | 'glow' | 'border';
  };
  autoAdvance?: {
    delay: number; // milliseconds
    trigger?: 'timer' | 'action';
  };
}

export interface Tour {
  id: string;
  title: string;
  description: string;
  category: string;
  steps: TourStep[];
  context: {
    pages: string[];
    modules: string[];
    roles: string[];
  };
  estimatedDuration: number; // minutes
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

interface HelpTourOverlayProps {
  tour: Tour | null;
  isActive: boolean;
  onClose: () => void;
  onComplete: (tourId: string) => void;
}

// Mock tours data
export const mockTours: Tour[] = [
  {
    id: 'dashboard-intro',
    title: 'Découverte du tableau de bord',
    description: 'Apprenez à naviguer et utiliser votre tableau de bord PLV+',
    category: 'getting-started',
    estimatedDuration: 3,
    difficulty: 'beginner',
    context: {
      pages: ['/dashboard'],
      modules: ['host'],
      roles: ['user', 'manager', 'admin']
    },
    steps: [
      {
        id: 'welcome',
        title: 'Bienvenue sur PLV+',
        content: 'Ce tour vous guidera à travers les fonctionnalités principales de votre tableau de bord. Vous apprendrez à naviguer, personnaliser et utiliser efficacement votre espace de travail.',
        target: 'body',
        position: 'center'
      },
      {
        id: 'sidebar',
        title: 'Navigation principale',
        content: 'La barre latérale contient tous les modules PLV+ disponibles. Cliquez sur les différentes sections pour explorer les fonctionnalités.',
        target: '[data-tour="sidebar"]',
        position: 'right',
        highlight: {
          element: '[data-tour="sidebar"]',
          style: 'glow'
        }
      },
      {
        id: 'widgets',
        title: 'Widgets du tableau de bord',
        content: 'Ces widgets affichent vos métriques importantes. Ils s\'adaptent automatiquement selon votre rôle et vos permissions.',
        target: '[data-tour="dashboard-widgets"]',
        position: 'top',
        highlight: {
          element: '[data-tour="dashboard-widgets"]',
          style: 'border'
        }
      },
      {
        id: 'pri-overview',
        title: 'Aperçu PRI+',
        content: 'Ce widget montre un résumé de vos activités PRI+ : calculs, marges, revenus. Cliquez dessus pour accéder aux détails.',
        target: '[data-tour="pri-overview"]',
        position: 'bottom',
        action: {
          type: 'hover',
          element: '[data-tour="pri-overview"]',
          description: 'Survolez le widget pour voir les actions disponibles'
        }
      },
      {
        id: 'help-button',
        title: 'Aide contextuelle',
        content: 'Ce bouton d\'aide flottant est toujours disponible. Il vous donne accès à l\'assistant IA, la documentation et le support.',
        target: '[data-tour="help-trigger"]',
        position: 'left',
        highlight: {
          element: '[data-tour="help-trigger"]',
          style: 'pulse'
        }
      }
    ]
  },
  {
    id: 'pri-plus-basics',
    title: 'Premiers pas avec PRI+',
    description: 'Découvrez comment créer votre premier calcul de prix avec PRI+',
    category: 'pri-plus',
    estimatedDuration: 5,
    difficulty: 'beginner',
    context: {
      pages: ['/pri-plus/calculations', '/pri-plus/dashboard'],
      modules: ['pri-plus'],
      roles: ['user', 'manager']
    },
    steps: [
      {
        id: 'pri-intro',
        title: 'Module PRI+',
        content: 'PRI+ est votre outil de calcul de prix de revient. Il vous aide à estimer précisément les coûts de vos projets PLV.',
        target: 'body',
        position: 'center'
      },
      {
        id: 'new-calculation',
        title: 'Créer un calcul',
        content: 'Commencez par cliquer sur ce bouton pour créer un nouveau calcul de prix. Vous pourrez ensuite saisir tous les éléments de coût.',
        target: '[data-tour="new-calculation-btn"]',
        position: 'bottom',
        action: {
          type: 'click',
          element: '[data-tour="new-calculation-btn"]',
          description: 'Cliquez pour commencer un nouveau calcul'
        }
      },
      {
        id: 'calculation-form',
        title: 'Formulaire de calcul',
        content: 'Ce formulaire vous guide étape par étape : informations générales, matériaux, main-d\'œuvre, frais généraux et marge.',
        target: '[data-tour="calculation-form"]',
        position: 'right'
      }
    ]
  }
];

export const HelpTourOverlay: React.FC<HelpTourOverlayProps> = ({ 
  tour, 
  isActive, 
  onClose, 
  onComplete 
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const currentStep = tour?.steps[currentStepIndex];

  // Position tooltip relative to target element
  useEffect(() => {
    if (!currentStep || !isActive) return;

    const positionTooltip = () => {
      const targetElement = document.querySelector(currentStep.target);
      const tooltip = tooltipRef.current;

      if (!targetElement || !tooltip) return;

      const targetRect = targetElement.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      switch (currentStep.position) {
        case 'top':
          top = targetRect.top - tooltipRect.height - 16;
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
          break;
        case 'bottom':
          top = targetRect.bottom + 16;
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
          break;
        case 'left':
          top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
          left = targetRect.left - tooltipRect.width - 16;
          break;
        case 'right':
          top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
          left = targetRect.right + 16;
          break;
        case 'center':
          top = (viewportHeight - tooltipRect.height) / 2;
          left = (viewportWidth - tooltipRect.width) / 2;
          break;
      }

      // Ensure tooltip stays within viewport
      top = Math.max(16, Math.min(top, viewportHeight - tooltipRect.height - 16));
      left = Math.max(16, Math.min(left, viewportWidth - tooltipRect.width - 16));

      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
    };

    // Add highlight to target element
    const addHighlight = () => {
      if (currentStep.highlight) {
        const element = document.querySelector(currentStep.highlight.element);
        if (element) {
          setHighlightedElement(element);
          element.classList.add('tour-highlight', `tour-highlight-${currentStep.highlight.style || 'glow'}`);
        }
      }
    };

    // Remove previous highlights
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight', 'tour-highlight-glow', 'tour-highlight-pulse', 'tour-highlight-border');
    });

    positionTooltip();
    addHighlight();

    // Scroll target into view
    const targetElement = document.querySelector(currentStep.target);
    if (targetElement && currentStep.target !== 'body') {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Auto-advance if configured
    let autoAdvanceTimer: NodeJS.Timeout;
    if (currentStep.autoAdvance && isPlaying) {
      autoAdvanceTimer = setTimeout(() => {
        nextStep();
      }, currentStep.autoAdvance.delay);
    }

    // Reposition on window resize
    const handleResize = () => positionTooltip();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (autoAdvanceTimer) clearTimeout(autoAdvanceTimer);
    };
  }, [currentStep, isActive, isPlaying]);

  // Cleanup highlights on unmount
  useEffect(() => {
    return () => {
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight', 'tour-highlight-glow', 'tour-highlight-pulse', 'tour-highlight-border');
      });
    };
  }, []);

  const nextStep = () => {
    if (!tour) return;
    
    if (currentStepIndex < tour.steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const goToStep = (index: number) => {
    if (tour && index >= 0 && index < tour.steps.length) {
      setCurrentStepIndex(index);
    }
  };

  const completeTour = () => {
    if (tour) {
      onComplete(tour.id);
    }
    closeTour();
  };

  const closeTour = () => {
    setCurrentStepIndex(0);
    setIsPlaying(false);
    onClose();
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  if (!tour || !isActive) return null;

  return (
    <>
      {/* Overlay backdrop */}
      <div 
        ref={overlayRef}
        className="fixed inset-0 z-50 bg-black bg-opacity-50"
        style={{ pointerEvents: 'none' }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-50 max-w-sm"
        style={{ pointerEvents: 'auto' }}
      >
        <Card className="p-4 shadow-xl border-2 border-blue-500">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {currentStepIndex + 1} / {tour.steps.length}
              </span>
            </div>
            <button
              onClick={closeTour}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {currentStep?.title}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {currentStep?.content}
            </p>
            
            {currentStep?.action && (
              <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center text-sm text-blue-800 dark:text-blue-300">
                  <Lightbulb className="w-4 h-4 mr-2" />
                  {currentStep.action.description}
                </div>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Progression</span>
              <span>{Math.round(((currentStepIndex + 1) / tour.steps.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStepIndex + 1) / tour.steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={prevStep}
                disabled={currentStepIndex === 0}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <button
                onClick={togglePlay}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              
              <button
                onClick={nextStep}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {currentStepIndex === tour.steps.length - 1 ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={closeTour}
                className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Ignorer
              </button>
              
              {currentStepIndex === tour.steps.length - 1 ? (
                <button
                  onClick={completeTour}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Terminer
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  Suivant
                </button>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* CSS for highlights - using inline styles instead of styled-jsx */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .tour-highlight {
            position: relative;
            z-index: 51;
          }
          
          .tour-highlight-glow {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3);
            border-radius: 8px;
          }
          
          .tour-highlight-pulse {
            animation: tour-pulse 2s infinite;
          }
          
          .tour-highlight-border {
            outline: 3px solid #3b82f6;
            outline-offset: 2px;
            border-radius: 8px;
          }
          
          @keyframes tour-pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
            }
          }
        `
      }} />
    </>
  );
};
